import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PiggyBank, TrendingUp, Plus, ArrowDownRight, ArrowUpRight, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { formatRupiah, formatDate } from "@/lib/dummyData";

const EMPTY_SAVINGS = { pokok: 0, wajib: 0, sukarela: 0, total: 0 };
import { SavingsAPI } from "@/lib/api";

const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// Label Indonesia untuk jenis simpanan dari backend.
const TYPE_LABEL = { POKOK: "Simpanan Pokok", WAJIB: "Simpanan Wajib", SUKARELA: "Simpanan Sukarela", SHU: "SHU Diterima", PENARIKAN: "Penarikan" };

export default function Savings() {
  const [tab, setTab] = useState("semua");
  const [savings, setSavings] = useState(EMPTY_SAVINGS);
  const [history, setHistory] = useState([]);

  // Muat simpanan & riwayat mutasi nyata milik anggota.
  useEffect(() => {
    SavingsAPI.mine().then((d) => {
      setSavings(d.savings);
      setHistory((d.history || []).map((h) => ({
        date: h.created_at,
        rawType: h.type,
        type: TYPE_LABEL[h.type] || h.type,
        amount: Number(h.amount),
        balance: h.balance_after != null ? Number(h.balance_after) : null,
      })));
    }).catch(() => {});
  }, []);

  const filter = (type) => history.filter((s) => (
    type === "semua" ? true :
    type === "pokok" ? s.rawType === "POKOK" :
    type === "wajib" ? s.rawType === "WAJIB" :
    s.rawType === "SUKARELA" || s.rawType === "SHU"
  ));

  // Grafik pertumbuhan: saldo kumulatif dari riwayat mutasi nyata (dalam ribuan).
  const chartData = (() => {
    const chrono = [...history].reverse(); // history datang DESC → jadikan kronologis
    let running = 0;
    return chrono.map((h) => {
      running += (h.rawType === "PENARIKAN" ? -h.amount : h.amount);
      const d = new Date(h.date);
      return { m: MONTHS_ID[d.getMonth()] || "", v: Math.round(running / 1000) };
    });
  })();

  return (
    <div className="space-y-7">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Buku Tabungan Digital</h1>
          <p className="text-ink/75 mt-2">Detail Simpanan Pokok, Wajib, dan Sukarela Anda — diperbarui real-time.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => toast.success("Mutasi diunduh")} variant="outline" className="rounded-full border-stone-soft" data-testid="savings-download">
            <Download size={15} /> Unduh Mutasi
          </Button>
          <Button asChild className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="savings-topup">
            <Link to="/anggota/tabungan/setor/sukarela"><Plus size={15} /> Tambah Simpanan</Link>
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid lg:grid-cols-3 gap-4">
        <SummaryCard label="Simpanan Pokok" value={savings.pokok} desc="Sekali bayar saat menjadi anggota" tint="bg-sage/15" />
        <SummaryCard
          label="Simpanan Wajib" value={savings.wajib} desc="Rp 100.000 / bulan" tint="bg-amber-warm/15"
          actions={[
            { label: "Bayar Simpanan Wajib", to: "/anggota/tabungan/setor/wajib", testid: "pay-wajib" },
          ]}
        />
        <SummaryCard
          label="Simpanan Sukarela" value={savings.sukarela} desc="Setoran bebas, dapat ditarik" tint="bg-forest/10"
          actions={[
            { label: "Setor", to: "/anggota/tabungan/setor/sukarela", testid: "pay-sukarela" },
            { label: "Tarik", to: "/anggota/tabungan/tarik", variant: "outline", testid: "withdraw-sukarela" },
          ]}
        />
      </div>

      {/* Chart */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-display font-semibold text-ink">Pertumbuhan Tabungan</h3>
            <p className="text-xs text-ink/55 mt-1">6 bulan terakhir · dalam ribuan rupiah</p>
          </div>
          <Badge className="bg-sage/20 text-forest hover:bg-sage/20 border-0 rounded-full">
            <TrendingUp size={11} /> {formatRupiah(savings.total || 0)}
          </Badge>
        </div>
        {chartData.length === 0 ? (
          <div className="h-64 grid place-items-center text-sm text-ink/55">Belum ada mutasi simpanan.</div>
        ) : (
        <div className="h-64 mt-4">
          <ResponsiveContainer>
            <AreaChart data={chartData}>
              <defs><linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2D5A27" stopOpacity={0.35} /><stop offset="100%" stopColor="#2D5A27" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
              <XAxis dataKey="m" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
              <Tooltip content={({ active, payload }) => active && payload?.[0] ? <div className="bg-white border border-stone-soft rounded-lg px-3 py-2 text-xs shadow-md"><div className="font-semibold">{payload[0].payload.m}</div><div className="text-forest">{formatRupiah(payload[0].value * 1000)}</div></div> : null} />
              <Area dataKey="v" stroke="#2D5A27" fill="url(#g2)" strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        )}
      </Card>

      {/* History */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4">Riwayat Mutasi</h3>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-cream rounded-full p-1">
            <TabsTrigger value="semua" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-semua">Semua</TabsTrigger>
            <TabsTrigger value="pokok" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-pokok">Pokok</TabsTrigger>
            <TabsTrigger value="wajib" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-wajib">Wajib</TabsTrigger>
            <TabsTrigger value="sukarela" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white" data-testid="tab-sukarela">Sukarela & SHU</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="pt-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-soft hover:bg-transparent">
                    <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Tanggal</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-ink/55">Jenis</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-ink/55 text-right">Nominal</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-ink/55 text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filter(tab).map((s, i) => (
                    <TableRow key={i} className="border-stone-soft hover:bg-cream/50" data-testid={`savings-row-${i}`}>
                      <TableCell className="text-sm">{formatDate(s.date)}</TableCell>
                      <TableCell className="text-sm font-medium text-ink flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-full grid place-items-center ${s.type.includes("SHU") ? "bg-amber-warm/20 text-amber-700" : "bg-forest/10 text-forest"}`}>
                          {s.type.includes("SHU") ? <ArrowDownRight size={13} /> : <ArrowUpRight size={13} />}
                        </span>
                        {s.type}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold text-forest">+ {formatRupiah(s.amount)}</TableCell>
                      <TableCell className="text-right text-sm text-ink/75">{formatRupiah(s.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

const SummaryCard = ({ label, value, desc, tint, actions = [] }) => (
  <Card className="rounded-2xl border-stone-soft p-6 flex flex-col">
    <div className={`w-11 h-11 rounded-xl grid place-items-center ${tint}`}><PiggyBank size={18} className="text-forest" /></div>
    <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold mt-4">{label}</div>
    <div className="font-display font-bold text-2xl text-ink mt-1">{formatRupiah(value)}</div>
    <div className="text-xs text-ink/75 mt-1">{desc}</div>
    {actions.length > 0 && (
      <div className="flex gap-2 mt-4 pt-4 border-t border-stone-soft">
        {actions.map((a) => (
          <Button
            key={a.to}
            asChild
            size="sm"
            variant={a.variant || "default"}
            className={`rounded-full flex-1 ${a.variant === "outline" ? "border-stone-soft" : "bg-forest hover:bg-forest-dark text-white"}`}
            data-testid={a.testid}
          >
            <Link to={a.to}>{a.label}</Link>
          </Button>
        ))}
      </div>
    )}
  </Card>
);
